import { Clear, Launch, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { EditActionCell } from "../../components/DataTable/customcell/EditActionCell";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store";
import {
  CompositeFilterDescriptor,
  DataResult,
  process,
} from "@progress/kendo-data-query";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import React from "react";
import { DataGrid } from "../../components/DataTable/DataGrid";
import { MapRateTypeModal } from "../Modals/map-rate-type-modal/MapRateModal";
import { getCalendarYears, getRateTypeMappings } from "../../slices/serviceagreements.slice";
import { getRateTypes } from "../../slices/productcostseeds.slice";

const initialFilters: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

export const RateTypeMapping = () => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState();
  const [selectedRateType, setSelectedRateType] = useState<string>("All");
  const exportRef = useRef<ExcelExport | null>(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilters);
  const [landlordSearchQuery, setLandlordSearchQuery] = useState<string>("");
  const { rateTypeMappings, calendarYears } = useAppSelector(
    (state: RootState) => state.serviceagreements
  );

  const { rateTypesArray } = useAppSelector(
    (state: RootState) => state.seedsdropdown
  );

  const gridColumns = [
    {
      field: "year",
      title: "Year",
      width: "150px",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "landLordName",
      title: "Landlord Name",
      width: "250px",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "rateType",
      title: "Rate Type",
      width: "180px",
      headerClassName: "super-app-theme--header",
    },
    {
      title: "Actions",
      width: "100px",
      headerClassName: "super-app-theme--header",
      cells: {
        data: (props: any) => (
          <EditActionCell {...props} onEditClick={onEditClick} />
        ),
      },
    },
  ];

  const onEditClick = (dataItem: any) => {
    setSelectedProduct(dataItem);
    setOpen(true);
  };

  useEffect(() => {
    dispatch(getCalendarYears());
    dispatch(getRateTypeMappings());
    dispatch(getRateTypes());
  }, []);

  
  const handleClearSearch = () => {
    setLandlordSearchQuery("");
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLandlordSearchQuery(event.target.value);
  };

   function handleYearChange(event: SelectChangeEvent<any>): void {
      setSelectedYear(event.target.value);
    }
  const excelExport = () => {
    if (exportRef.current !== null) {
      const processedData: DataResult = process(rateTypeMappings, {
        filter: filter,
      });
      exportRef.current.save(processedData);
    }
  };

  useEffect(() => {
    if (landlordSearchQuery || selectedRateType || selectedYear) {
      const filterObj: CompositeFilterDescriptor = {
        logic: "and",
        filters: [],
      };
      if (selectedRateType !== "All") {
        filterObj.filters.push({
          field: "rateType",
          operator: "eq",
          value: selectedRateType,
        });
      }
      if (landlordSearchQuery) {
        filterObj.filters.push({
          field: "landLordName",
          operator: "contains",
          value: landlordSearchQuery,
        });
      }
      if (selectedYear) {
        filterObj.filters.push({
          field: "year",
          operator: "eq",
          value: selectedYear,
        });
      }
      setFilter(filterObj);
    } else {
      setFilter(initialFilters);
    }
  }, [landlordSearchQuery, selectedRateType, selectedYear]);


  const handleClose = () => {
    dispatch(getRateTypeMappings());
    setOpen(false);
  };

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2}>
        <FormControl>
          <InputLabel> Year </InputLabel>
          <Select
            labelId=""
            value={selectedYear}
            label="Year"
            onChange={handleYearChange}
            size="small"
          >
            {calendarYears.map((type: any) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <InputLabel>Rate Type</InputLabel>
          <Select
            label="Rate Type"
            value={selectedRateType}
            onChange={(e: SelectChangeEvent) =>
              setSelectedRateType(e.target.value)
            }
            size="small"
            sx={{ minWidth: "200px" }}
          >
            <MenuItem value="All">All</MenuItem>
            {rateTypesArray.map((rateObj) => (
              <MenuItem key={rateObj.id} value={rateObj.name}>
                {rateObj.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          placeholder="Search Landlord"
          id="outlined-basic"
          variant="outlined"
          size="small"
          value={landlordSearchQuery}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={handleClearSearch}
                style={landlordSearchQuery ? {} : { visibility: "hidden" }}
              >
                <Clear />
              </IconButton>
            ),
            startAdornment: <Search />,
          }}
        />
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={excelExport}
        >
          Export
        </Button>
      </Box>
      <Box>
        <DataGrid
          ref={exportRef}
          data={rateTypeMappings}
          dataItemKey="id"
          filter={filter}
          columns={gridColumns}
        />
      </Box>
      {open && (
        <MapRateTypeModal
          open={open}
          handleClose={handleClose}
          editItem={selectedProduct}
        />
      )}
    </Box>
  );
};
